import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { confirmProposal, rejectProposal } from '../../../services/api/agentMemoryApi';

/**
 * "Ska jag komma ihåg det här?" — the inline decision, in the conversation that produced it.
 *
 * WHY INLINE. The proposal comes out of something the user just said, and the decision is
 * cheapest to make while that sentence is still on screen. Sending them to Inställningar → Vad
 * Finn vet to approve it means leaving the conversation, finding the right row, and remembering
 * what it referred to — so in practice it does not get done, and the memory the platform learned
 * sits unconfirmed and unused.
 *
 * WHAT IT DOES NOT CHANGE. The authority rule is untouched: assistant output alone never creates
 * durable memory, and this is still an explicit human confirm — the same endpoint the settings
 * page calls. Inline changes WHERE the decision is made, never WHETHER one is required.
 *
 * The text shown is the proposal's own `displayText`, verbatim. It is what a confirm writes, so
 * showing anything more polished would mean approving one sentence and storing another.
 */
export default function MemoryProposalCard({ proposals, companyId, isAdmin }) {
    // proposalId → 'confirmed' | 'rejected' | 'failed'. Decisions are per proposal: a turn can
    // produce several, and approving one says nothing about the others.
    const [decided, setDecided] = useState({});
    const [busy, setBusy] = useState(null);

    const decide = async (proposal, decision) => {
        setBusy(proposal.id);
        try {
            await (decision === 'confirmed' ? confirmProposal : rejectProposal)(companyId, proposal.id);
            setDecided((d) => ({ ...d, [proposal.id]: decision }));
        } catch {
            setDecided((d) => ({ ...d, [proposal.id]: 'failed' }));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="finn-memory-proposal">
            {proposals.map((p) => {
                const state = decided[p.id];
                // A company fact is the company's, so confirming one needs an Owner/Admin (the
                // gateway enforces this — memory/authz.ts). Offering a button that always fails
                // would be worse than pointing at the page where the right person can act.
                const mayDecide = p.scope !== 'company' || isAdmin;

                if (state === 'confirmed') {
                    return (
                        <div key={p.id} className="finn-memory-proposal-done">
                            ✓ Sparat — Finn använder det här framöver.
                        </div>
                    );
                }
                if (state === 'rejected') {
                    return (
                        <div key={p.id} className="finn-memory-proposal-done">
                            Förslaget avvisades — inget sparades.
                        </div>
                    );
                }

                return (
                    <div key={p.id} className="finn-memory-proposal-row">
                        <div className="finn-memory-proposal-text">
                            <span aria-hidden="true">💡</span> Ska jag komma ihåg det här?
                            <div className="finn-memory-proposal-claim">”{p.displayText}”</div>
                            {p.scope === 'company' && (
                                <div className="finn-memory-proposal-scope">Gäller hela företaget</div>
                            )}
                        </div>
                        {mayDecide ? (
                            <div className="finn-memory-proposal-actions">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    disabled={busy === p.id}
                                    onClick={() => decide(p, 'confirmed')}
                                >
                                    {busy === p.id ? 'Sparar…' : 'Spara'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link text-muted"
                                    disabled={busy === p.id}
                                    onClick={() => decide(p, 'rejected')}
                                >
                                    Nej tack
                                </button>
                            </div>
                        ) : (
                            <div className="finn-memory-proposal-scope">
                                Bara ägare eller administratör kan spara företagsuppgifter.
                            </div>
                        )}
                        {state === 'failed' && (
                            <div className="finn-memory-proposal-error">
                                Det gick inte att spara just nu.{' '}
                                <Link to={`/${companyId}/vad-finn-vet`}>Försök under Vad Finn vet</Link>.
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
